"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import PatientSearch from "@/components/patient/patient-search";
import Button from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useLocations } from "@/hooks/use-locations";

import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";

import { PatientSummary } from "@/types/patient";

import { useOpportunityMutations } from "@/api/provider/opportunities/opportunities-query";
import {
  useCampaigns,
  usePipelines,
} from "@/api/provider/opportunities/pipelines-query";

interface AddOpportunityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPipelineId?: string;
}

// Optional-field preprocessing. HTML inputs always return strings, so
// we trim the value first: leading/trailing whitespace would otherwise
// fail email validation, and a whitespace-only value would land in the
// DB as a "present" but useless string.
const trimToUndef = (v: unknown): unknown => {
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" ? undefined : t;
  }
  return v == null ? undefined : v;
};
const optionalEmail = z.preprocess(
  trimToUndef,
  z.string().email("Enter a valid email address").optional(),
);
const optionalString = z.preprocess(trimToUndef, z.string().optional());

const opportunitySchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: optionalString,
  email: optionalEmail,
  phoneNumber: optionalString,
  campaignId: optionalString,
  locationId: optionalString,
  optedInToSmsMarketing: z.boolean(),
  optedInToEmailMarketing: z.boolean(),
});

type OpportunityFormValues = z.infer<typeof opportunitySchema>;

const MARKETING_CONSENT_CHANNELS = [
  {
    name: "optedInToSmsMarketing",
    label: "Opted in to SMS marketing",
    description: "They agreed to receive marketing texts.",
  },
  {
    name: "optedInToEmailMarketing",
    label: "Opted in to email marketing",
    description: "They agreed to receive marketing emails.",
  },
] as const satisfies ReadonlyArray<{
  name: keyof OpportunityFormValues;
  label: string;
  description: string;
}>;

export function AddOpportunityDialog({
  open,
  onOpenChange,
  defaultPipelineId,
}: AddOpportunityDialogProps) {
  const [patient, setPatient] = useState<PatientSummary | undefined>();

  const { data: pipelines = [] } = usePipelines();
  const { data: campaignsData } = useCampaigns();
  const campaigns = campaignsData?.data ?? [];
  const { locations } = useLocations({ seeAllLocations: true });
  const { create } = useOpportunityMutations();

  const hasPipelines = pipelines.length > 0;
  // The board being viewed, when there is one. The form never asks: a campaign
  // routes to its own pipeline (or none), and no campaign lands on the default.
  const viewedPipelineId = pipelines.find(
    (pipeline) => pipeline.id === defaultPipelineId,
  )?.id;

  const form = useForm<OpportunityFormValues>({
    resolver: zodResolver(opportunitySchema),
    // Validate on change once a field has been touched — gives inline
    // email-format feedback without yelling at the user before they've
    // had a chance to type.
    mode: "onTouched",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      campaignId: "",
      locationId: "",
      optedInToSmsMarketing: false,
      optedInToEmailMarketing: false,
    },
  });

  const resetForm = () => {
    form.reset({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      campaignId: "",
      locationId: "",
      optedInToSmsMarketing: false,
      optedInToEmailMarketing: false,
    });
    setPatient(undefined);
  };

  const onSubmit = (values: OpportunityFormValues) => {
    create.mutate(
      {
        firstName: values.firstName,
        lastName: values.lastName || undefined,
        email: values.email || undefined,
        phoneNumber: values.phoneNumber || undefined,
        campaignId: values.campaignId || undefined,
        locationId: values.locationId || undefined,
        patientId: patient?.id || undefined,
        // Adding from a board keeps the card on that board; otherwise the server
        // routes it — a chosen campaign's pipeline (not enrolled if it has
        // none), else the clinic default.
        pipelineId: viewedPipelineId,
        // With no pipeline configured there is nothing to route to, and the
        // request would be refused rather than recording the person.
        unassigned: !hasPipelines,
        optedInToSmsMarketing: values.optedInToSmsMarketing,
        optedInToEmailMarketing: values.optedInToEmailMarketing,
      },
      {
        onSuccess: () => {
          toast({ title: "Lead created" });
          onOpenChange(false);
          resetForm();
        },
        onError: async (error) => {
          toast({
            variant: "error",
            title: "Failed to create lead",
            description: await parseError(error),
          });
        },
      },
    );
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Add Lead</DrawerTitle>
          <DrawerDescription>
            Set a campaign and they join that campaign&apos;s pipeline.
            Otherwise they start on your default one.
          </DrawerDescription>
        </DrawerHeader>

        <DrawerBody>
          <form
            id="add-opportunity-form"
            className="space-y-4"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="add-opportunity-first-name" mandatory>
                  First Name
                </Label>
                <Input
                  id="add-opportunity-first-name"
                  {...form.register("firstName")}
                  placeholder="Jane"
                  hasError={!!form.formState.errors.firstName}
                  errorMessage={form.formState.errors.firstName?.message}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="add-opportunity-last-name">Last Name</Label>
                <Input
                  id="add-opportunity-last-name"
                  {...form.register("lastName")}
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="add-opportunity-email">Email</Label>
              <Input
                id="add-opportunity-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="jane@example.com"
                {...form.register("email")}
                hasError={!!form.formState.errors.email}
                errorMessage={form.formState.errors.email?.message}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="add-opportunity-phone">Phone Number</Label>
              <Input
                id="add-opportunity-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="(555) 123-4567"
                {...form.register("phoneNumber")}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="add-opportunity-campaign">Campaign</Label>
              <Controller
                control={form.control}
                name="campaignId"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="add-opportunity-campaign">
                      <SelectValue placeholder="Select campaign (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="add-opportunity-location">Location</Label>
              <Controller
                control={form.control}
                name="locationId"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="add-opportunity-location">
                      <SelectValue placeholder="Select location (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <div>
                <h4 className="text-body-strong text-foreground">
                  Marketing consent
                </h4>
                <p className="text-caption text-foreground-muted">
                  Leave a channel unticked to keep whatever preference this
                  person already has.
                </p>
              </div>

              {MARKETING_CONSENT_CHANNELS.map((channel) => (
                <div key={channel.name} className="flex items-start gap-2">
                  <Controller
                    control={form.control}
                    name={channel.name}
                    render={({ field }) => (
                      <Checkbox
                        id={`add-opportunity-${channel.name}`}
                        className="mt-0.5"
                        checked={field.value === true}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                    )}
                  />
                  <label
                    htmlFor={`add-opportunity-${channel.name}`}
                    className="cursor-pointer"
                  >
                    <span className="block text-body text-foreground">
                      {channel.label}
                    </span>
                    <span className="block text-caption text-foreground-muted">
                      {channel.description}
                    </span>
                  </label>
                </div>
              ))}
            </div>

            <div className="space-y-1">
              <Label>Link to Patient</Label>
              <PatientSearch
                patient={patient}
                ariaLabel="Link to patient"
                onPatientSelect={setPatient}
                placeholder="Search for patient (optional)"
                showCreatePatient={false}
              />
            </div>
          </form>
        </DrawerBody>

        <DrawerFooter>
          <DrawerClose asChild>
            <Button variant="secondary" disabled={create.isPending}>
              Cancel
            </Button>
          </DrawerClose>
          <Button
            type="submit"
            form="add-opportunity-form"
            variant="primary"
            disabled={!form.formState.isValid && form.formState.isSubmitted}
            isLoading={create.isPending}
            loadingText="Adding lead..."
          >
            Add Lead
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
